// Actions
import { ClearAccessTokenAction, FetchAccessTokenAction, FetchAccessTokenFailedAction, FetchAccessTokenSucceededAction } from "@ms/uno-actions/lib/local/auth/AccessTokenActions";
// Modules
import { BaseLogicModule } from "../BaseLogicModule";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { isTokenExpiryWithinBufferPeriod } from "@ms/uno-utilities/lib/local/AuthUtilities";
import { LocalIdGenerator } from "@ms/uno-utilities/lib/local/LocalIdGenerator";
export class AuthModule extends BaseLogicModule {
    get name() {
        return "AuthModule";
    }
    async refreshAccessTokenAsync(resource, claims, silent) {
        this.loggers.traceLogger.logTrace(0x1e45d554 /* tag_4r3vu */ , TraceLevel.Verbose, `Refresh Access Token Started for [Resource=${resource}]`);
        const action = new ClearAccessTokenAction([
            `${resource}/.default`
        ]);
        await this.dispatcher.dispatchActionAsync(action);
        return this.fetchAccessTokenInternal({
            resource,
            claims,
            silent
        });
    }
    async fetchAccessTokenAsync(resource, claims, silent) {
        this.loggers.traceLogger.logTrace(0x1e45d557 /* tag_4r3vx */ , TraceLevel.Verbose, `Fetch Access Token [Resource=${resource}]`);
        return this.fetchAccessTokenAsyncFromStoreOrServer({
            resource,
            claims,
            silent
        });
    }
    async fetchAccessTokenForScopesAsync(scopes, claims, silent) {
        this.loggers.traceLogger.logTrace(0x1e08f217 /* tag_4cpix */ , TraceLevel.Verbose, `Fetch Access Token [Scopes=${scopes}]`);
        return this.fetchAccessTokenAsyncFromStoreOrServer({
            scopes,
            claims,
            silent
        });
    }
    async fetchAccessTokenAsyncFromStoreOrServer(params) {
        const scopes = "scopes" in params ? params.scopes : [
            `${params.resource}/.default`
        ];
        const tokenInfo = this.authStore.getAccessToken(scopes, params.claims);
        if (tokenInfo.data != null) {
            const expiryBuffer = this.config.settings.auth.authTokenExpiryBufferSeconds;
            const validationResult = !isTokenExpiryWithinBufferPeriod(tokenInfo.data.expiry, expiryBuffer);
            // If the token is not expired or within expiry buffer period, return it
            if (validationResult) {
                this.loggers.traceLogger.logTrace(0x1e08f216 /* tag_4cpiw */ , TraceLevel.Verbose, `Fetch Access Token Success [Scopes=${scopes}][Source=Store][Expiry=${tokenInfo.data.expiry?.toISOString()}][Buffer=${expiryBuffer}][Valid=${validationResult}]`);
                return Promise.resolve(tokenInfo.data);
            }
            this.loggers.traceLogger.logTrace(0x1e08f215 /* tag_4cpiv */ , TraceLevel.Info, `Fetch Access Token Validation Failure [Scopes=${scopes}][Source=Store][Expiry=${tokenInfo.data.expiry?.toISOString()}][Buffer=${expiryBuffer}][Valid=${validationResult}]`);
        }
        // If the token is expired or within expiry buffer period, fetch a new one
        return this.fetchAccessTokenInternal(params);
    }
    async fetchAccessTokenInternal(params) {
        const { claims, silent } = params;
        const actionId = LocalIdGenerator.generate();
        const scopesArray = "scopes" in params ? params.scopes : [
            `${params.resource}/.default`
        ];
        const action = new FetchAccessTokenAction(actionId, scopesArray, claims);
        await this.dispatcher.dispatchActionAsync(action);
        this.loggers.traceLogger.logTrace(0x1e259847 /* tag_4jz7h */ , TraceLevel.Verbose, `Fetch Access Token started for [Scopes=${scopesArray}][Silent=${silent}]`);
        try {
            const accessTokenWithExpiry = await this.authService.fetchAccessTokenAsync(params);
            this.loggers.traceLogger.logTrace(0x1e45d553 /* tag_4r3vt */ , TraceLevel.Verbose, `Fetch Access Token Success [Scopes=${scopesArray}][Source=AuthService]`);
            const action = new FetchAccessTokenSucceededAction(actionId, scopesArray, accessTokenWithExpiry.accessToken, accessTokenWithExpiry.expiry, claims);
            await this.dispatcher.dispatchActionAsync(action);
            return Promise.resolve(accessTokenWithExpiry);
        } catch (error) {
            const failedRequestResult = error;
            this.loggers.traceLogger.logTrace(0x1e45d552 /* tag_4r3vs */ , TraceLevel.Error, `Fetch Access token Failure [Scopes=${scopesArray}][Source=AuthService][Subsource=${failedRequestResult.response == null ? "Host" : "OBOService"}]`);
            const action = new FetchAccessTokenFailedAction(actionId, scopesArray, claims, failedRequestResult.error ?? undefined);
            await this.dispatcher.dispatchActionAsync(action);
            return Promise.reject(failedRequestResult);
        }
    }
    /**
     * Initializes an instance of an Entity Module
     * @param dispatcher The dispatcher the SPA uses to dispatch actions
     * @param services Providers for the services
     * @param stores Providers for the stores
     * @param loggers Loggers for telemetry
     * @param configProvider Delegate to retrieve the app configuration
     */ constructor(dispatcher, authService, authStore, loggers, configProvider){
        super(dispatcher, loggers, configProvider);
        this.authStore = authStore;
        this.authService = authService;
    }
}
